import React, { useCallback, useEffect } from 'react';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import Body from 'ui/Typography/Body';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { showHamburgerMenu, resetLayout } from 'ducks/layout';
import {
  isUnsavedChangesSheetVisible,
  hideUnsavedChangesSheet,
  isLoadVpcSheetVisibleOnExit,
  setLoadVpcSheetVisible,
  setLoadVpcSheetVisibleOnExit,
  showSaveVpcSheet,
  isOpenDesignSheetVisibleOnExit,
  setOpenDesignSheetVisibleOnExit,
  showOpenDesignSheet,
  unsavedChangesLeaveAction,
  showSaveDesignToProfileSheet
} from 'ducks/sheets';
import { getVpcCode, invalidateVpcCode } from 'ducks/vpc';
import { useCore } from 'contexts/core';
import { useIntl } from 'react-intl';
import { useNavigationBlock } from 'contexts/navigationBlock';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import VpcText from 'ui/VpcText/VpcText';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import RtlStyles from 'i18n/rtl';
import {
  KompisSheetBody,
  KompisSheetFooterButtons,
  KompisSheetFooter,
  KompisSheetBodyPadding
} from '@inter-ikea-kompis/react-components';
import Title from 'ui/Typography/Title';
import SheetHeader from 'ui/Sheet/SheetHeader';
import Sheet from 'ui/Sheet/Sheet';
import useRestartPlanner from 'hooks/useRestartPlanner';
import { useSceneHandler } from 'contexts/sceneHandler';
import { useAppDispatch } from 'ducks/hooks';
import { getUserName } from 'ducks/userInfo';

import Modal, { ModalFooter, ModalHeader, Prompt } from '@ingka/modal';

import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/modal/dist/style.css';
import '@ingka/focus/dist/style.css';

import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { useVpc } from 'hooks/useVpc';
import { ShapeTypeEnum } from '@inter-ikea-storageone/range';
import { setShape } from 'ducks/helpLines';
import { setAutofillActive } from 'rangeConfig/platsa/features/Autofill/Autofill';
import { getDesignId, resetDesignId } from 'ducks/designId';
import { useSaveDesign } from 'hooks/useSaveDesign';
import { useServiceSettings } from 'contexts/serviceSettings';

const Buttons = styled(Box)`
  display: flex;
`;

type ModalProps = {
  onClose: () => void;
  onLeave: () => void;
  saveVpc: () => Promise<void>;
  visible?: boolean;
};

const ScreensaverModal = ({ saveVpc, visible }: ModalProps) => {
  const vpcCode = useSelector(getVpcCode);
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (visible && !vpcCode) {
      saveVpc();
    }
  }, [dispatch, saveVpc, visible, vpcCode]);

  return (
    <>
      <Body mb="xs" type={ThemeFontStyleTypeEnum.bodyMedium} data-testid="unsaved-changes-modal">
        {formatMessage({ id: 'storageone.screenSaverUnsavedChangesModalContent' })}
      </Body>
      <VpcText />
    </>
  );
};

const DefaultSheet = ({ onClose, onLeave, saveVpc }: ModalProps) => {
  const isVisible = useSelector(isUnsavedChangesSheetVisible);
  const dispatch = useAppDispatch();

  const onSave = async () => {
    onClose();
    saveVpc();
  };

  const showGroupNav = () => {
    onClose();
    setTimeout(() => dispatch(showHamburgerMenu()), 10);
  };

  const { formatMessage } = useIntl();
  return (
    <Sheet onOverlayClick={() => onClose()} visible={isVisible}>
      <SheetHeader backIcon={true} onCloseButtonClick={onClose} onBackButtonClick={showGroupNav} />
      <KompisSheetBody theme={SkapaTheme}>
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <Title pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
            {formatMessage({ id: 'storageone.unsavedChanges' })}
          </Title>
          <Body mb="l" type={ThemeFontStyleTypeEnum.bodySmall}>
            {formatMessage({ id: 'storageone.unsavedChangesConfirmMessage' })}
          </Body>
        </KompisSheetBodyPadding>
      </KompisSheetBody>
      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          compact={false}
          theme={SkapaTheme}
          data-testid="unsaved-changes-buttons"
          primaryLabel={formatMessage({ id: 'storageone.leave' })}
          secondaryLabel={formatMessage({ id: 'kompis.checkOutSave' })}
          onButtonClick={event => {
            if (event.detail.type === 'primaryButton') {
              onLeave();
            } else {
              onSave();
            }
          }}
        />
      </KompisSheetFooter>
    </Sheet>
  );
};

const UnsavedChangesSheet = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const isVisible = useSelector(isUnsavedChangesSheetVisible);
  const coreApi = useCore();
  const { leaveRoute } = useNavigationBlock();
  const triggeredByScreensaver = history.location.pathname === '/planner/screensaver';
  const vpcCode = useSelector(getVpcCode);
  const showLoadVpcSheet = useSelector(isLoadVpcSheetVisibleOnExit);
  const showOpenDesignSheetAfterExit = useSelector(isOpenDesignSheetVisibleOnExit);
  const restartPlanner = useRestartPlanner();
  const customLeaveAction = useSelector(unsavedChangesLeaveAction);
  const vpc = useVpc();
  const designId = useSelector(getDesignId);
  const { setLocalWallShape, clearLocalScene } = useSceneHandler();
  const { enableSaveToProfile } = useServiceSettings().dexfSettings.general;
  const userInfo = useSelector(getUserName);
  const userLoggedIn = enableSaveToProfile && userInfo.userName !== undefined;

  const { saveToProfile } = useSaveDesign();

  const onContinue = () => {
    onClose();
    dispatch(invalidateVpcCode());
    dispatch(resetDesignId());
  };

  const onClose = useCallback(() => {
    dispatch(setLoadVpcSheetVisibleOnExit(false));
    dispatch(setOpenDesignSheetVisibleOnExit(false));
    dispatch(hideUnsavedChangesSheet());
    if (triggeredByScreensaver) {
      history.replace(AppRoutes.PLANNER);
    }
  }, [dispatch, history, triggeredByScreensaver]);

  const clearWallShape = () => {
    dispatch(setShape(ShapeTypeEnum.off));
    setLocalWallShape({ type: ShapeTypeEnum.off, dimensions: [] });
    dispatch(setAutofillActive(false));
  };

  const onLeave = () => {
    if (typeof customLeaveAction !== 'undefined') {
      onClose();
      customLeaveAction();
    } else {
      dispatch(resetLayout());
      restartPlanner();
      clearWallShape();
      clearLocalScene();
      onClose();

      if (showLoadVpcSheet) {
        dispatch(setLoadVpcSheetVisible(true));
      } else if (showOpenDesignSheetAfterExit) {
        dispatch(showOpenDesignSheet(true));
      } else {
        leaveRoute();
      }
    }
  };

  const saveVpc = async () => {
    if (!vpcCode) {
      await vpc.save({
        doNotTakeScreenshots: triggeredByScreensaver,
        userTriggered: !triggeredByScreensaver
      });
    }

    if (!triggeredByScreensaver) {
      if (enableSaveToProfile) {
        if (userLoggedIn) {
          saveToProfile();
        } else {
          dispatch(showSaveDesignToProfileSheet());
        }
      } else {
        dispatch(showSaveVpcSheet(false));
      }
    }
  };

  useEffect(() => {
    const onChange = () => {
      if (vpcCode) {
        dispatch(invalidateVpcCode());
      }
      if (designId) {
        dispatch(resetDesignId());
      }
    };

    return coreApi.subscribe('history', onChange);
  }, [coreApi, designId, dispatch, vpcCode]);

  return (
    <>
      {triggeredByScreensaver && isVisible ? (
        <Modal visible={isVisible} escapable={true} handleCloseBtn={onClose}>
          <Prompt
            aria-labelledby="UnsavedChangesPrompt"
            title={formatMessage({ id: 'storageone.unsavedChanges' })}
            titleId="UnsavedChangesPrompt"
            header={<ModalHeader ariaCloseTxt="Close prompt" />}
            footer={
              <ModalFooter>
                <Buttons data-testid="unsaved-changes-buttons" mt="0">
                  <Button
                    onClick={onContinue}
                    dataTestId="unsaved-changes-continue"
                    minWidth="8rem"
                    pr="md"
                    text={formatMessage({ id: 'storageone.continuePlanning' })}
                    type={ButtonTypeEnum.emphasised}
                  />
                  <Button
                    onClick={onLeave}
                    minWidth="8rem"
                    dataTestId="unsaved-changes-back"
                    text={formatMessage({ id: 'storageone.backToGallery' })}
                    type={ButtonTypeEnum.secondary}
                  />
                </Buttons>
              </ModalFooter>
            }
          >
            <RtlStyles>
              <Box pr="xxl">
                <ScreensaverModal
                  onClose={onClose}
                  onLeave={onLeave}
                  saveVpc={saveVpc}
                  visible={isVisible}
                />
              </Box>
            </RtlStyles>
          </Prompt>
        </Modal>
      ) : (
        <DefaultSheet onClose={onClose} onLeave={onLeave} saveVpc={saveVpc} />
      )}
    </>
  );
};

export default UnsavedChangesSheet;
